import type { Organization, Project, Task, Team, User } from '../types';

const normalizeRole = (role?: string): string => {
  return (role || '').trim().toLowerCase();
};

const normalizeIdentity = (value?: string | null): string => {
  return (value || '').trim().toLowerCase();
};

const addIdentityVariants = (target: Set<string>, value?: string | null): void => {
  const normalized = normalizeIdentity(value);
  if (!normalized) {
    return;
  }

  target.add(normalized);

  const withoutCommonPrefix = normalized.replace(/^user[-_]/, '');
  if (withoutCommonPrefix) {
    target.add(withoutCommonPrefix);
  }

  const compact = normalized.replace(/[^a-z0-9]/g, '');
  if (compact) {
    target.add(compact);
  }

  if (normalized.includes('@')) {
    const localPart = normalized.split('@')[0];
    if (localPart) {
      target.add(localPart);
      target.add(localPart.replace(/[^a-z0-9]/g, ''));
    }
  }
};

const getUserIdentitySet = (user: User | null | undefined): Set<string> => {
  const identities = new Set<string>();

  addIdentityVariants(identities, user?.id);
  addIdentityVariants(identities, user?.email);

  return identities;
};

export function getProjectTeamMemberIds(
  project?: Project | null,
  teams: Team[] = []
): Set<string> {
  const memberIds = new Set<string>();

  (project?.teamMembers || []).forEach((member) => {
    addIdentityVariants(memberIds, String(member.userId || ''));
  });

  const targetTeamIds = new Set(
    (project?.teamIds || [])
      .map((teamId) => normalizeIdentity(teamId))
      .filter(Boolean)
  );

  if (targetTeamIds.size > 0) {
    teams.forEach((team) => {
      const normalizedTeamId = normalizeIdentity(team.id);
      if (!targetTeamIds.has(normalizedTeamId)) {
        return;
      }

      (team.memberIds || []).forEach((memberId) => {
        addIdentityVariants(memberIds, memberId);
      });
    });
  }

  return memberIds;
}

export function getProjectTeamUsers(
  project: Project | null | undefined,
  users: User[],
  teams: Team[] = []
): User[] {
  const memberIds = getProjectTeamMemberIds(project, teams);

  if (memberIds.size === 0) {
    return [];
  }

  return users.filter((user) => {
    const identities = getUserIdentitySet(user);
    return Array.from(identities).some((identity) => memberIds.has(identity));
  });
}

export function getProjectMemberRole(
  project: Project | null | undefined,
  userId: string,
  userEmail?: string
): string | null {
  const userIdentities = new Set<string>();
  addIdentityVariants(userIdentities, userId);
  addIdentityVariants(userIdentities, userEmail);

  const membership = project?.teamMembers?.find((member) => {
    const memberIdentities = new Set<string>();
    addIdentityVariants(memberIdentities, String(member.userId || ''));
    return Array.from(memberIdentities).some((identity) => userIdentities.has(identity));
  });

  if (!membership) {
    return null;
  }

  return normalizeRole(String(membership.role));
}

export function isProjectAdminOrOwner(
  project: Project | null | undefined,
  userId: string,
  userEmail?: string
): boolean {
  const role = getProjectMemberRole(project, userId, userEmail);
  return role === 'admin' || role === 'owner';
}

export function isProjectStakeholderRole(role: string | null | undefined): boolean {
  return normalizeRole(role || '') === 'stakeholder';
}

export function canManageProjectSettings(
  user: User | null | undefined,
  project: Project | null | undefined
): boolean {
  if (!user || !project) {
    return false;
  }

  if (normalizeRole(user.role) === 'admin') {
    return true;
  }

  const role = getProjectMemberRole(project, user.id, user.email);
  return role === 'admin' || role === 'owner';
}

export function canCreateProjectTasks(
  user: User | null | undefined,
  project: Project | null | undefined
): boolean {
  if (!user || !project) {
    return false;
  }

  if (normalizeRole(user.role) === 'admin') {
    return true;
  }

  if (isExternalPartnerUser(user, project)) {
    return false;
  }

  if (canManageProjectSettings(user, project)) {
    return true;
  }

  const projectRole = getProjectMemberRole(project, user.id, user.email);
  return Boolean(projectRole);
}

export function canEditTaskParameters(
  user: User | null | undefined,
  project: Project | null | undefined
): boolean {
  if (!user || !project) {
    return false;
  }

  if (canManageProjectSettings(user, project)) {
    return true;
  }

  const projectRole = getProjectMemberRole(project, user.id, user.email);
  if (!projectRole) {
    return false;
  }

  return !isProjectStakeholderRole(projectRole);
}

export function canViewProjectFinance(
  user: User | null | undefined,
  project: Project | null | undefined
): boolean {
  if (!user || !project) {
    return false;
  }

  if (user.role === 'admin') {
    return true;
  }

  const role = getProjectMemberRole(project, user.id, user.email);
  // Only project owners can view costs (restrict from stakeholders and team members)
  return role === 'admin' || role === 'owner';
}

export function canEditProjectFinance(
  user: User | null | undefined,
  project: Project | null | undefined
): boolean {
  if (!user || !project) {
    return false;
  }

  if (normalizeRole(user.role) === 'admin') {
    return true;
  }

  const role = getProjectMemberRole(project, user.id, user.email);
  return role === 'admin' || role === 'owner';
}

export function canEditProjectAccountingUi(
  user: User | null | undefined,
  project: Project | null | undefined,
  organization?: Organization | null
): boolean {
  if (!user || !project) {
    return false;
  }

  const identities = getUserIdentitySet(user);

  const inProjectFinance = (project.projectFinanceUserIds || []).some((memberId) => {
    const candidate = new Set<string>();
    addIdentityVariants(candidate, memberId);
    return Array.from(candidate).some((id) => identities.has(id));
  });

  if (inProjectFinance) {
    return true;
  }

  const inGlobalFinance = (organization?.globalFinanceUserIds || []).some((memberId) => {
    const candidate = new Set<string>();
    addIdentityVariants(candidate, memberId);
    return Array.from(candidate).some((id) => identities.has(id));
  });

  return inGlobalFinance;
}

export function canViewProjectAccountingUi(
  user: User | null | undefined,
  project: Project | null | undefined,
  organization?: Organization | null
): boolean {
  if (!user || !project) {
    return false;
  }

  if (canEditProjectAccountingUi(user, project, organization)) {
    return true;
  }

  if (normalizeRole(user.role) === 'investor') {
    return true;
  }

  const projectRole = getProjectMemberRole(project, user.id, user.email);
  return projectRole === 'investor';
}

export function isExternalPartnerUser(
  user: User | null | undefined,
  project?: Project | null
): boolean {
  if (!user) {
    return false;
  }

  if (normalizeRole(user.role) === 'external_partner') {
    return true;
  }

  if (!project) {
    return false;
  }

  const projectRole = getProjectMemberRole(project, user.id, user.email);
  return projectRole === 'external_partner';
}

export function canDeleteProjectTask(
  user: User | null | undefined,
  project: Project | null | undefined
): boolean {
  if (!user || !project) {
    return false;
  }

  if (normalizeRole(user.role) === 'admin') {
    return true;
  }

  if (isExternalPartnerUser(user, project)) {
    return false;
  }

  return canEditTaskParameters(user, project);
}

export function canViewTask(
  user: User | null | undefined,
  task: Task | null | undefined,
  project?: Project | null
): boolean {
  if (!user || !task) {
    return false;
  }

  const hasExternalPartnerScope = isExternalPartnerUser(user, project);
  if (!hasExternalPartnerScope) {
    return true;
  }

  const userIdentities = getUserIdentitySet(user);
  const assigneeIds = Array.isArray(task.assigneeIds) ? task.assigneeIds : [];

  if (assigneeIds.length === 0) {
    return false;
  }

  return assigneeIds.some((assigneeId) => {
    const assigneeIdentities = new Set<string>();
    addIdentityVariants(assigneeIdentities, assigneeId);
    return Array.from(assigneeIdentities).some((identity) => userIdentities.has(identity));
  });
}

export function filterVisibleTasksForUser<T extends Task>(
  user: User | null | undefined,
  tasks: T[],
  resolveTaskProject?: (task: T) => Project | null | undefined
): T[] {
  return tasks.filter((task) => canViewTask(user, task, resolveTaskProject?.(task)));
}
